import React, { useState, useRef } from "react";
import apiClient from "../../../../../api/apiClient";

const REQUIRED_FIELDS = ["firstName", "lastName", "email"];

// Outbound: full original field set (unchanged)
const OPTIONAL_FIELDS_OUTBOUND = [
    "phone", "company", "jobTitle", "industry",
    "website", "linkedIn", "address", "city", "state", "zipCode",
    "source", "priority", "status", "estimatedValue", "estimatedCloseDate", "notes",
];

// Inbound: map CSV columns for location + lead type only.
const OPTIONAL_FIELDS_INBOUND = ["state", "city", "leadType"];

const FIELD_LABELS = {
    firstName: "First Name", lastName: "Last Name", email: "Email",
    state: "State", city: "City", leadType: "Lead Type",
    priority: "Priority", status: "Status",
    phone: "Phone", company: "Company", jobTitle: "Job Title",
    industry: "Industry", website: "Website", linkedIn: "LinkedIn",
    address: "Address", zipCode: "Zip Code",
    source: "Lead Source", estimatedValue: "Estimated Value ($)",
    estimatedCloseDate: "Est. Close Date", notes: "Notes",
};

const STEPS = ["Upload", "Map Columns", "Preview", "Result"];

const ImportLeadsModal = ({ onClose, onImported, endpoint = "/admin/crm/leads/import" }) => {
    const isInbound = endpoint.includes("/inbound/");
    const OPTIONAL_FIELDS = isInbound ? OPTIONAL_FIELDS_INBOUND : OPTIONAL_FIELDS_OUTBOUND;
    const ALL_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];

    const [step, setStep]       = useState(0);
    const [file, setFile]       = useState(null);
    const [headers, setHeaders] = useState([]);
    const [preview, setPreview] = useState([]);
    const [mapping, setMapping] = useState({});
    const [importing, setImporting] = useState(false);
    const [result, setResult]   = useState(null);
    const [error, setError]     = useState("");
    const inputRef = useRef();

    const handleFile = (f) => {
        if (!f) return;
        if (!f.name.endsWith(".csv") && f.type !== "text/csv") {
            setError("Only CSV files are supported");
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            setError("File exceeds 5 MB limit");
            return;
        }
        setFile(f);
        setError("");
        const reader = new FileReader();
        reader.onload = (e) => {
            const lines = e.target.result.split(/\r?\n/).filter(Boolean);
            if (lines.length < 2) { setError("CSV must have a header row and at least one data row"); return; }
            const parseRow = (line) => {
                const out = [];
                let cur = "", inQuotes = false;
                for (const ch of line) {
                    if (ch === '"') { inQuotes = !inQuotes; }
                    else if (ch === "," && !inQuotes) { out.push(cur.trim()); cur = ""; }
                    else { cur += ch; }
                }
                out.push(cur.trim());
                return out;
            };
            const hdrs = parseRow(lines[0]);
            const rows = lines.slice(1, 6).map(parseRow);
            setHeaders(hdrs);
            setPreview(rows);
            const autoMap = {};
            ALL_FIELDS.forEach((field) => {
                const idx = hdrs.findIndex((h) =>
                    h.toLowerCase().replace(/[\s_-]/g, "") === field.toLowerCase() ||
                    h.toLowerCase().replace(/[\s_-]/g, "").includes(field.toLowerCase())
                );
                if (idx >= 0) autoMap[field] = hdrs[idx];
            });
            setMapping(autoMap);
            setStep(1);
        };
        reader.readAsText(f);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files[0]);
    };

    const mappingValid = REQUIRED_FIELDS.every((f) => mapping[f]);

    const handleImport = async () => {
        setImporting(true);
        setError("");
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("mapping", JSON.stringify(mapping));
            const res = await apiClient.post(endpoint, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(res.data);
            setStep(3);
            onImported && onImported();
        } catch (err) {
            setError(err.response?.data?.error || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="crm-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="crm-modal crm-modal--lg">
                <div className="crm-modal__header">
                    <h2 className="crm-modal__title">Import Leads from CSV</h2>
                    <button className="crm-modal__close" onClick={onClose}>×</button>
                </div>
                <div className="crm-modal__body">
                    {/* Steps */}
                    <div className="crm-wizard-steps">
                        {STEPS.map((s, i) => (
                            <React.Fragment key={s}>
                                <div className={`crm-wizard-step ${i < step ? "done" : i === step ? "active" : ""}`}>
                                    <div className="crm-wizard-step-num">{i < step ? "✓" : i + 1}</div>
                                    <span>{s}</span>
                                </div>
                                {i < STEPS.length - 1 && <div className="crm-wizard-step-sep" />}
                            </React.Fragment>
                        ))}
                    </div>

                    {error && <p style={{ color: "#ef4444", marginBottom: 12 }}>{error}</p>}

                    {/* Step 0: Upload */}
                    {step === 0 && (
                        <div
                            className="crm-import-dropzone"
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            onClick={() => inputRef.current?.click()}
                        >
                            <p style={{ fontSize: 16, marginBottom: 8 }}>Drop CSV here or click to browse</p>
                            <p style={{ fontSize: 12 }}>Max 500 rows · 5 MB · CSV only</p>
                            <input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }}
                                onChange={(e) => handleFile(e.target.files[0])} />
                        </div>
                    )}

                    {/* Step 1: Map columns */}
                    {step === 1 && (
                        <div>
                            <p style={{ fontSize: 13, marginBottom: 16, color: "#6b7280" }}>
                                Map your CSV columns to lead fields. Required fields are marked *.
                            </p>
                            <div className="crm-mapping-grid">
                                {ALL_FIELDS.map((field) => (
                                    <div key={field} className="admin-form-group">
                                        <label>{FIELD_LABELS[field] || field}{REQUIRED_FIELDS.includes(field) ? " *" : ""}</label>
                                        <select
                                            className="admin-input"
                                            value={mapping[field] || ""}
                                            onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value || undefined }))}
                                        >
                                            <option value="">— skip —</option>
                                            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                                        </select>
                                    </div>
                                ))}
                            </div>

                        </div>
                    )}

                    {/* Step 2: Preview */}
                    {step === 2 && (
                        <div>
                            <p style={{ fontSize: 13, marginBottom: 8, color: "#6b7280" }}>
                                First 5 rows preview (mapped fields only):
                            </p>
                            <div style={{ overflowX: "auto" }}>
                                <table className="crm-lead-table">
                                    <thead>
                                        <tr>
                                            {ALL_FIELDS.filter((f) => mapping[f]).map((f) => (
                                                <th key={f}>{FIELD_LABELS[f] || f}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.map((row, i) => (
                                            <tr key={i}>
                                                {ALL_FIELDS.filter((f) => mapping[f]).map((f) => {
                                                    const colIdx = headers.indexOf(mapping[f]);
                                                    return <td key={f}>{row[colIdx] || "—"}</td>;
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Result */}
                    {step === 3 && result && (
                        <div className="crm-import-result">
                            <div className="crm-import-result-row crm-import-result-row--success">
                                <span>Imported</span>
                                <strong>{result.imported}</strong>
                            </div>
                            <div className="crm-import-result-row crm-import-result-row--warn">
                                <span>Duplicates skipped</span>
                                <strong>{result.duplicates}</strong>
                            </div>
                            <div className="crm-import-result-row crm-import-result-row--error">
                                <span>Errors</span>
                                <strong>{result.errors}</strong>
                            </div>
                            {result.errorDetails?.length > 0 && (
                                <details style={{ fontSize: 12, marginTop: 8 }}>
                                    <summary style={{ cursor: "pointer", color: "#6b7280" }}>Show error details</summary>
                                    <ul style={{ marginTop: 8, paddingLeft: 16 }}>
                                        {result.errorDetails.map((e, i) => (
                                            <li key={i}>Row {e.row}: {e.reason}</li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}
                </div>

                <div className="crm-modal__footer">
                    {step === 0 && <button className="admin-btn" onClick={onClose}>Cancel</button>}
                    {step === 1 && (
                        <>
                            <button className="admin-btn" onClick={() => setStep(0)}>← Back</button>
                            <button className="admin-btn admin-btn--primary" disabled={!mappingValid} onClick={() => setStep(2)}>
                                Preview →
                            </button>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <button className="admin-btn" onClick={() => setStep(1)}>← Back</button>
                            <button className="admin-btn admin-btn--primary" onClick={handleImport} disabled={importing}>
                                {importing ? "Importing…" : "Import Now"}
                            </button>
                        </>
                    )}
                    {step === 3 && (
                        <button className="admin-btn admin-btn--primary" onClick={onClose}>Done</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportLeadsModal;
